import { PreviewCallLog } from "@/schema"
import formatPhone from "@/utils/format-phone"
import { format } from "date-fns"
import { ArrowDownLeft, ArrowUpRight, Calendar, Clock, Phone } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"

import PeekViewSkeleton from "./skeleton"

function formatDuration(seconds: number | null) {
  if (!seconds) return null
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins === 0) return `${secs}s`
  return `${mins}m ${secs}s`
}

export default function CallLogPeekView({ callLogId }: { callLogId: string; href: string | null }) {
  const { data: callLog, isLoading } = usePeekViewData(callLogId, "CallLog") as {
    data: PreviewCallLog
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton className="max-w-80" />

  const dateText = callLog.started_at
    ? format(
        callLog.started_at,
        new Date(callLog.started_at).getFullYear() === new Date().getFullYear()
          ? "MMM d · h:mm a"
          : "MMM d, yyyy · h:mm a"
      )
    : "Unknown"
  const isOutgoing = callLog.direction === "outgoing"
  const duration = formatDuration(callLog.duration_seconds)

  return (
    <div className="text-tertiary-foreground flex w-full max-w-80 flex-col items-stretch gap-2">
      <div className="mb-0.5 flex items-baseline gap-2">
        {isOutgoing ? (
          <ArrowUpRight className="text-blue-accent mt-0.5 size-4 flex-none" />
        ) : (
          <ArrowDownLeft className="text-green-accent mt-0.5 size-4 flex-none" />
        )}
        <p className="line-clamp-2 font-medium">{isOutgoing ? "Outgoing call" : "Incoming call"}</p>
      </div>
      <div className="flex items-center gap-2">
        <Calendar className="text-icon size-4 flex-none" />
        <p className="text-sm">{dateText}</p>
      </div>
      {duration && (
        <div className="flex items-center gap-2">
          <Clock className="text-icon size-4 flex-none" />
          <p className="text-sm">{duration}</p>
        </div>
      )}
      {callLog.phone_number && (
        <div className="flex items-center gap-2">
          <Phone className="text-icon size-4 flex-none" />
          <p className="truncate text-sm">{formatPhone(callLog.phone_number)}</p>
        </div>
      )}
    </div>
  )
}
